<app-navbar></app-navbar>
<div class="option-category-manager">
  <!-- Loading Overlay -->
  <div class="loading-overlay" *ngIf="isLoading">
    <div class="spinner"></div>
    <p>Loading data...</p>
  </div>

  <!-- Header -->
  <header class="header">
    <h1>Option Category Manager</h1>
    <p class="subtitle">Manage behavior option categories</p>
  </header>

  <!-- Main Content -->
  <div class="content-container" *ngIf="!isLoading">
    
    <!-- Two Column Layout -->
    <div class="two-column-layout">
      
      <!-- Left Column: Category List -->
      <aside class="category-list-panel">
        <div class="panel-header">
          <h3>List of Categories</h3>
          <button
            class="btn-add-category"
            (click)="openNewCategoryDialog()"
            title="Add new category">
            <span class="icon">+</span>
            New Category
          </button>
        </div>

        <div class="category-list">
          <div
            *ngFor="let category of categories"
            class="category-item"
            [class.selected]="selectedCategory?.categoryId === category.categoryId"
            (click)="selectCategory(category)">
            <div class="category-item-content">
              <span class="category-name">{{ category.categoryName }}</span>
              <span class="subcategory-id-badge">{{ category.subcategoryId }}</span>
              <div class="mapping-preview">
                <span class="mapping-badge">
                  {{ category['subcatId-mapping'] || 'No mapping' }}
                </span>
                <span class="option-badge">
                  Option {{ category.option || '-' }}
                </span>
              </div>
              <span class="category-description-preview">
                {{ category.categoryDescription || 'No description' }}
              </span>
            </div>
          </div>

          <div class="empty-state" *ngIf="categories.length === 0">
            <p>No categories found. Click "New Category" to create one.</p>
          </div>
        </div>
      </aside>

      <!-- Right Column: Category Editor -->
      <main class="category-editor-panel">
        <div *ngIf="!selectedCategory" class="empty-state-large">
          <div class="empty-icon">📁</div>
          <h3>No Category Selected</h3>
          <p>Select a category from the list to view and edit details</p>
        </div>

        <div *ngIf="selectedCategory && editedCategory" class="editor-content">
          <!-- Editor Header -->
          <div class="editor-header">
            <div class="editor-title">
              <h2>{{ isNewCategory ? 'New Category' : 'Edit Category' }}</h2>
              <div class="editor-badges">
                <span class="category-id-badge" *ngIf="!isNewCategory">
                  ID: {{ editedCategory.categoryId }}
                </span>
                <span class="subcategory-badge" *ngIf="editedCategory.subcategoryId">
                  {{ editedCategory.subcategoryId }}
                </span>
              </div>
            </div>
            
            <div class="editor-actions">
              <button
                class="btn-delete"
                *ngIf="!isNewCategory"
                (click)="deleteCategory()"
                [disabled]="isSaving">
                <span class="icon">🗑️</span>
                Delete
              </button>
              <button
                class="btn-secondary"
                (click)="cancelEditing()"
                [disabled]="isSaving">
                Cancel
              </button>
              <button
                class="btn-primary"
                (click)="saveCategory()"
                [disabled]="!isFormValid() || isSaving">
                <span *ngIf="!isSaving">{{ isNewCategory ? 'Create Category' : 'Update Category' }}</span>
                <span *ngIf="isSaving">Saving...</span>
              </button>
            </div>
          </div>

          <!-- Category Form -->
          <div class="category-form">
            <div class="form-group">
              <label for="subcategoryId">Subcategory ID *</label>
              <input
                id="subcategoryId"
                type="text"
                class="form-input"
                [(ngModel)]="editedCategory.subcategoryId"
                placeholder="Enter subcategory ID (e.g., core, advanced, specialist)"
                [disabled]="isSaving || !isNewCategory">
              <span class="field-hint">A unique identifier for this subcategory (cannot be changed after creation)</span>
            </div>

            <div class="form-group">
              <label for="categoryName">Category Name *</label>
              <input
                id="categoryName"
                type="text"
                class="form-input"
                [(ngModel)]="editedCategory.categoryName"
                placeholder="Enter category name"
                [disabled]="isSaving">
              <span class="field-hint">A unique name for this category</span>
            </div>

            <div class="form-group">
              <label for="categoryDescription">Category Description</label>
              <textarea
                id="categoryDescription"
                class="form-textarea"
                [(ngModel)]="editedCategory.categoryDescription"
                placeholder="Enter category description (optional)"
                rows="6"
                [disabled]="isSaving"></textarea>
              <span class="field-hint">Provide a detailed description of this category</span>
            </div>

            <div class="mapping-grid">
              <div class="form-group">
                <label for="subcatIdMapping">Subcategory ID Mapping</label>
                <select
                  id="subcatIdMapping"
                  class="form-input"
                  [(ngModel)]="editedCategory['subcatId-mapping']"
                  [disabled]="isSaving || isLoadingSubtypes">
                  <option value="">
                    {{ isLoadingSubtypes ? 'Loading subtypes...' : 'Select behavior subtype' }}
                  </option>
                  <option
                    *ngFor="let subtype of subTypeList"
                    [value]="subtype.subTypeId">
                    {{ subtype.subType }} ({{ subtype.subTypeId }})
                  </option>
                </select>
                <span class="field-hint">Maps this option to the behavioral level key used for scoring</span>
              </div>

              <div class="form-group">
                <label for="option">Option</label>
                <input
                  id="option"
                  type="text"
                  class="form-input"
                  [(ngModel)]="editedCategory.option"
                  placeholder="e.g., 4"
                  [disabled]="isSaving">
                <span class="field-hint">The selected option value stored in the assessment level object</span>
              </div>
            </div>
          </div>

          <!-- Unsaved Changes Warning -->
          <div class="unsaved-changes-warning" *ngIf="hasUnsavedChanges()">
            <span class="warning-icon">⚠️</span>
            You have unsaved changes. Click "{{ isNewCategory ? 'Create' : 'Update' }} Category" to save.
          </div>
        </div>
      </main>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <div class="modal-overlay" *ngIf="showDeleteConfirmation" (click)="showDeleteConfirmation = false">
    <div class="modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>Confirm Deletion</h3>
        <button class="btn-close" (click)="showDeleteConfirmation = false">×</button>
      </div>
      
      <div class="modal-body">
        <p class="modal-description">
          Are you sure you want to delete the category <strong>"{{ selectedCategory?.categoryName }}"</strong>
          <span *ngIf="selectedCategory?.subcategoryId"> ({{ selectedCategory?.subcategoryId }})</span>?
        </p>
        <p class="modal-warning">
          This action cannot be undone.
        </p>
      </div>
      
      <div class="modal-footer">
        <button
          class="btn-secondary"
          (click)="showDeleteConfirmation = false"
          [disabled]="isSaving">
          Cancel
        </button>
        <button
          class="btn-danger"
          (click)="confirmDelete()"
          [disabled]="isSaving">
          <span *ngIf="!isSaving">Delete Category</span>
          <span *ngIf="isSaving">Deleting...</span>
        </button>
      </div>
    </div>
  </div>
</div>
<app-footer></app-footer>
